import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, Steuersatz, SupplierContract, SupplierContractTerm, SupplierContractTermInput,
  SupplierContractTermPreview } from "@kundeninfo/api-client";
import { Arbeitsdialog, darfAbbrechen, useBeforeUnload, useFormularZustand } from "@kundeninfo/ui";
import { Auswahl, Eingabe, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { apiMeldung } from "../../components/vorgangsdetail.js";
import { formatDatum } from "../../format.js";
import { errorProp } from "../../components/fieldErrors.js";
import { AMOUNT_FIELD_MAX, DUE_DAY_MAX, DUE_OFFSET_MAX_DAYS, TAX_CASES, TERM_NOTE_MAX, amountField, amountForApi,
  fieldErrorsOf, perCycleText, wholeNumber } from "./shared.js";
import type { Cycle } from "./shared.js";
import { SUPPLIER_TEXT_WEB as T } from "./texts.js";
import "./Suppliers.css";

/**
 * DAS KONDITIONSPANEL „Kondition ändern ab …“ (Spec „Laufzeit und Konditionen nach validFrom“, „Anker der
 * Fälligkeiten“; Plan Aufgabe 13) und die Felder einer Kondition, die das Vertragspanel für die erste Kondition teilt.
 *
 * Modalpanel rechts (`Arbeitsdialog panel`, unter 1024 px vollflächig), vorbelegt mit der geltenden Kondition. Vor dem
 * Speichern zeigt es die Vorschau des Servers (P20: der Anker rückt auf den nächsten Posten der Vorgängerin) als Satz
 * „wirksam ab …, erster Posten mit neuem Betrag fällig …“; Fachregeln der Vorschau (nach dem Vertragsende, Monat bei
 * jährlich festem Tag) stehen schon dann am Feld. Der Speichern-Knopf ist die Bestätigung (Dirk 216c), Abbrechen der
 * eine Ausgang daneben (Rückfrage bei Ungespeichertem, `useBeforeUnload`); eine 409 zeigt den fremden Stand mit „Neu
 * laden“. Ein gespeicherter, nicht mehr aktiver Steuersatz bleibt als Option stehen (Lehre aus Review 17).
 */
type DueRule = SupplierContractTerm["dueRule"];
type TaxCase = SupplierContractTerm["taxCase"];

const CYCLES: readonly Cycle[] = ["MONTHLY", "QUARTERLY", "HALF_YEARLY", "YEARLY"];
const DUE_RULES: readonly DueRule[] = ["SUBPERIOD_START", "SUBPERIOD_END", "FIXED_DAY"];
const MONTH_NUMBERS = Array.from({ length: 12 }, (_, i) => i + 1);
/** Die Vorschau läuft kurz nach der letzten Eingabe, nicht je Tastendruck. */
const PREVIEW_DELAY_MS = 250;
/** Die Felder des Zeitplans, an denen die Vorschau hängt (Datum, Rhythmus, Regel, Tag, Monat, Versatz, Satz). */
const SCHEDULE_KEYS = ["validFrom", "cycle", "dueRule", "dueDayOfMonth", "dueMonth", "dueOffsetDays", "taxRateId"] as const;
const SCHEDULE_FIELDS: readonly string[] = ["validFrom", "dueDayOfMonth", "dueOffsetDays", "taxRateId"];

/** Die Felder einer Kondition, wie die Maske sie führt (Text, bis gesendet wird). */
export type TermDraft = {
  validFrom: string; cycle: Cycle; dueRule: DueRule; dueDay: string; dueMonth: string; offset: string; net: string;
  taxCase: TaxCase; taxRateId: string; note: string;
};

/** Die Feldnamen des Servers (`term.<feld>` ohne, `firstTerm.<feld>` mit Präfix). */
export const TERM_FIELDS = ["validFrom", "cycle", "dueRule", "dueDayOfMonth", "dueMonth", "dueOffsetDays", "netAmount",
  "taxCase", "taxRateId", "note"] as const;

export function termDraftOf(t: SupplierContractTerm | null, cycle: Cycle = "MONTHLY"): TermDraft {
  if (t === null) {
    return { validFrom: "", cycle, dueRule: "SUBPERIOD_START", dueDay: "", dueMonth: "", offset: "0", net: "",
      taxCase: "DOMESTIC", taxRateId: "", note: "" };
  }
  return {
    validFrom: "", cycle: t.cycle, dueRule: t.dueRule, dueDay: t.dueDayOfMonth === null ? "" : String(t.dueDayOfMonth),
    dueMonth: t.dueMonth === null ? "" : String(t.dueMonth), offset: String(t.dueOffsetDays), net: amountField(t.netAmount),
    taxCase: t.taxCase, taxRateId: t.taxRateId, note: t.note ?? "",
  };
}

/** Der Steuersatz einer Kondition: der gewählte, ohne Wahl der höchste aktive (Regelsatz). */
const rateOf = (d: TermDraft, rates: readonly Steuersatz[]): string => (d.taxRateId !== "" ? d.taxRateId
  : rates.reduce<Steuersatz | null>((m, r) => (m === null || r.ratePercent > m.ratePercent ? r : m), null)?.id ?? "");
const fixedDay = (d: TermDraft): boolean => d.dueRule === "FIXED_DAY";
const yearlyFixed = (d: TermDraft): boolean => fixedDay(d) && d.cycle === "YEARLY";

/** Die Prüfung der Maske vor dem Senden — die Grenzen des Schemas (Aufgabe 12), Schlüssel wie die Felder des Servers. */
export function termFieldErrors(d: TermDraft, rates: readonly Steuersatz[], prefix: string, withValidFrom: boolean):
    Record<string, string> {
  const e: Record<string, string> = {};
  if (withValidFrom && d.validFrom === "") e[`${prefix}validFrom`] = T.dateRequired;
  if (fixedDay(d) && typeof wholeNumber(d.dueDay, 1, DUE_DAY_MAX) !== "number") {
    e[`${prefix}dueDayOfMonth`] = T.wholeNumber(1, DUE_DAY_MAX);
  }
  if (wholeNumber(d.offset, -DUE_OFFSET_MAX_DAYS, DUE_OFFSET_MAX_DAYS) === undefined) {
    e[`${prefix}dueOffsetDays`] = T.wholeNumber(-DUE_OFFSET_MAX_DAYS, DUE_OFFSET_MAX_DAYS);
  }
  if (amountForApi(d.net) === null) e[`${prefix}netAmount`] = T.amountInvalid;
  if (rateOf(d, rates) === "") e[`${prefix}taxRateId`] = T.choiceRequired;
  return e;
}

/** Die Kondition für den Server (ohne `validFrom`) — nur nach einer Prüfung ohne Befund. Tag und Monat gehen nur mit,
 *  wo die Regel sie kennt (sonst meldete die Domäne „gehört nur zu …“). */
export function termBody(d: TermDraft, rates: readonly Steuersatz[]): Omit<SupplierContractTermInput, "validFrom"> {
  return {
    cycle: d.cycle, dueRule: d.dueRule,
    dueMonth: yearlyFixed(d) && d.dueMonth !== "" ? Number(d.dueMonth) : null,
    dueDayOfMonth: fixedDay(d) ? Number(d.dueDay) : null,
    dueOffsetDays: wholeNumber(d.offset, -DUE_OFFSET_MAX_DAYS, DUE_OFFSET_MAX_DAYS) ?? 0,
    netAmount: amountForApi(d.net)!, taxRateId: rateOf(d, rates), taxCase: d.taxCase,
    note: d.note.trim() === "" ? null : d.note.trim(),
  };
}

/** Die Felder einer Kondition (Raster). */
export function TermFields(p: {
  draft: TermDraft; onDraft: (patch: Partial<TermDraft>) => void; rates: ActiveRates;
  errors: Readonly<Record<string, string>>; prefix: string; withValidFrom: boolean;
}): ReactElement {
  const { draft: d, onDraft: set } = p;
  const at = (f: (typeof TERM_FIELDS)[number]) => errorProp(p.errors[`${p.prefix}${f}`]);
  const rate = rateOf(d, p.rates.rates);
  return (
    <div className="sup__grid">
      {p.withValidFrom && (
        <Eingabe art="datum" beschriftung={T.termFrom} wert={d.validFrom} pflicht {...at("validFrom")}
          onGeaendert={(e) => set({ validFrom: e.wert })} />
      )}
      <Auswahl beschriftung={T.cycle} wert={d.cycle} {...at("cycle")} onGeaendert={(e) => set({ cycle: e.wert as Cycle })}>
        {CYCLES.map((c) => <option key={c} value={c}>{T.cycles[c]}</option>)}
      </Auswahl>
      <Auswahl beschriftung={T.dueRuleLabel} wert={d.dueRule} {...at("dueRule")}
        onGeaendert={(e) => set({ dueRule: e.wert as DueRule })}>
        {DUE_RULES.map((r) => <option key={r} value={r}>{T.dueRules[r]}</option>)}
      </Auswahl>
      {fixedDay(d) && (
        <Eingabe art="zahl" beschriftung={T.dueDay} wert={d.dueDay} pflicht {...at("dueDayOfMonth")}
          onGeaendert={(e) => set({ dueDay: e.wert })} />
      )}
      {yearlyFixed(d) && (
        <Auswahl beschriftung={T.dueMonth} wert={d.dueMonth} {...at("dueMonth")} onGeaendert={(e) => set({ dueMonth: e.wert })}>
          <option value="">{T.choose}</option>
          {MONTH_NUMBERS.map((m) => <option key={m} value={String(m)}>{T.month(m)}</option>)}
        </Auswahl>
      )}
      <Eingabe art="zahl" beschriftung={T.dueOffset} einheit={T.days} wert={d.offset} {...at("dueOffsetDays")}
        onGeaendert={(e) => set({ offset: e.wert })} />
      <Eingabe art="dezimal" beschriftung={T.netAmount} einheit={T.perCycle[d.cycle]} wert={d.net} pflicht
        maxLength={AMOUNT_FIELD_MAX} {...at("netAmount")} onGeaendert={(e) => set({ net: e.wert })} />
      <Auswahl beschriftung={T.taxCaseLabel} wert={d.taxCase} {...at("taxCase")}
        onGeaendert={(e) => set({ taxCase: e.wert as TaxCase })}>
        {TAX_CASES.map((c) => <option key={c} value={c}>{T.taxCase[c]}</option>)}
      </Auswahl>
      <Auswahl beschriftung={T.taxRate} wert={rate} {...at("taxRateId")} onGeaendert={(e) => set({ taxRateId: e.wert })}>
        {rate === "" && <option value="">{T.choose}</option>}
        {rate !== "" && !p.rates.rates.some((r) => r.id === rate) && <option value={rate}>{storedRateText(p.rates)}</option>}
        {p.rates.rates.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
      </Auswahl>
      <Eingabe className="sup__wide" beschriftung={T.termNote} mehrzeilig wert={d.note} maxLength={TERM_NOTE_MAX}
        {...at("note")} onGeaendert={(e) => set({ note: e.wert })} />
    </div>
  );
}

/** Die aktiven Steuersätze für die Auswahl samt Ladezustand: Erst nach dem Laden ist ein gespeicherter Satz, der fehlt,
 *  als „nicht mehr aktiv“ erkannt; ein Fehlschlag meldet das Panel (Review 13, W3). */
export type ActiveRates = { rates: Steuersatz[]; loaded: boolean; failed: boolean };

export function useActiveRates(client: Pick<ApiClient, "listActiveTaxRates">, enabled = true): ActiveRates {
  const [state, setState] = useState<ActiveRates>({ rates: [], loaded: false, failed: false });
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    client.listActiveTaxRates().then((r) => { if (alive) setState({ rates: r.items, loaded: true, failed: false }); },
      () => { if (alive) setState({ rates: [], loaded: false, failed: true }); });
    return () => { alive = false; };
  }, [client, enabled]);
  return state;
}

/** Die Beschriftung eines gespeicherten Satzes, der in der aktiven Liste fehlt: erst geladen „nicht mehr aktiv“ (auch in
 *  der Erfassung einer Eingangsrechnung, Final-Fixrunde Teil C). */
export const storedRateText = (r: ActiveRates): string => (r.loaded ? T.inactiveRate : r.failed ? T.storedRate : T.pending);

export type TermPanelClient = Pick<ApiClient,
  "previewContractTerm" | "addContractTerm" | "getSupplierContract" | "listActiveTaxRates">;

type Preview = { key: string; result: SupplierContractTermPreview | null; errors: Record<string, string>; failed: string | null };

/** Die Kondition, von der die neue ausgeht: die geltende, sonst die jüngste. */
const baseTerm = (c: SupplierContract): SupplierContractTerm | null => c.currentTerm ?? c.terms.at(-1) ?? null;

export function KonditionPanel(p: {
  customerId: string; contract: SupplierContract; client: TermPanelClient;
  onGespeichert: (c: SupplierContract) => void; onSchliessen: (reload: boolean) => void;
  /** Ein Schritt im Browserverlauf (Entscheid 696, PF9): die Zurück-Taste schließt das Panel wie Abbrechen. */
  verlauf?: boolean;
}): ReactElement {
  const { client, customerId } = p;
  const [contract, setContract] = useState(p.contract);
  const [start, setStart] = useState(() => termDraftOf(baseTerm(p.contract)));
  const [draft, setDraft] = useState(start);
  const [tried, setTried] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const rateState = useActiveRates(client);
  const rates = rateState.rates;

  const local = termFieldErrors(draft, rates, "", true);
  const input: SupplierContractTermInput | null = Object.keys(local).length > 0 ? null
    : { validFrom: draft.validFrom, ...termBody(draft, rates) };
  // Die Vorschau hängt nur am Zeitplan (K6): Betrag und Notiz ändern Anker und erste Fälligkeit nicht — ein noch
  // ungültiger Betrag geht als 0 mit, damit die Daten trotzdem vorab stehen.
  const scheduleReady = SCHEDULE_FIELDS.every((f) => local[f] === undefined);
  const previewInput: SupplierContractTermInput | null = !scheduleReady ? null : { validFrom: draft.validFrom,
    ...termBody({ ...draft, net: amountForApi(draft.net) === null ? "0" : draft.net, note: "" }, rates) };
  const key = previewInput === null ? "" : JSON.stringify(SCHEDULE_KEYS.map((k) => previewInput[k]));

  useEffect(() => {
    if (previewInput === null) return undefined;
    let alive = true;
    const timer = setTimeout(() => {
      client.previewContractTerm(customerId, contract.id, previewInput).then(
        (result) => { if (alive) setPreview({ key, result, errors: {}, failed: null }); },
        (e: unknown) => {
          if (!alive) return;
          const errors = fieldErrorsOf(e);
          setPreview({ key, result: null, errors, failed: Object.keys(errors).length > 0 ? null : apiMeldung(e, T.actionFailed) });
        });
    }, PREVIEW_DELAY_MS);
    return () => { alive = false; clearTimeout(timer); };
    // Die Eingabe IST der Schlüssel; Klient und Lieferant wechseln im offenen Panel nicht.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, contract.id]);

  const form = useFormularZustand<SupplierContract>({
    speichern: () => client.addContractTerm(customerId, contract.id, { version: contract.version, term: input! }),
    neuLaden: async () => {
      const c = await client.getSupplierContract(customerId, contract.id);
      const d = termDraftOf(baseTerm(c));
      setContract(c);
      setStart(d);
      setDraft(d);
      setTried(false);
    },
    bekannteFelder: TERM_FIELDS,
  });

  // Nach einem fremden Stand (409) lädt das Schließen die Liste neu (K3) — sie trägt sonst die alte Version.
  const conflicted = useRef(false);
  useEffect(() => { if (form.zustand === "fremder-stand") conflicted.current = true; }, [form.zustand]);

  const current = preview !== null && preview.key === key ? preview : null;
  const errors = { ...(tried ? local : {}), ...(current?.errors ?? {}), ...form.feldFehler };
  const dirty = JSON.stringify(draft) !== JSON.stringify(start);
  useBeforeUnload(dirty);

  async function save(): Promise<void> {
    setTried(true);
    if (input === null) return;
    const r = await form.aktion();
    if (r !== undefined) p.onGespeichert(r);
  }
  async function leave(): Promise<void> {
    if (form.laeuft) return;
    if (await darfAbbrechen(dirty)) p.onSchliessen(conflicted.current);
  }

  const base = baseTerm(contract);
  const messages = [
    ...(form.meldung === null ? [] : [<Hinweis key="m" art="fehler">{form.meldung}</Hinweis>]),
    ...(current === null || current.failed === null ? [] : [<Hinweis key="p" art="warnung">{current.failed}</Hinweis>]),
    ...(rateState.failed ? [<Hinweis key="r" art="warnung">{T.ratesLoadFailed}</Hinweis>] : []),
  ];
  return (
    <Arbeitsdialog titel={T.changeTermTitle(contract.title)} panel="supplier-term"
      {...(p.verlauf === true ? { verlauf: true } : {})} onAbbrechen={leave}
      abbrechenGesperrt={form.laeuft} {...(messages.length === 0 ? {} : { meldungen: <>{messages}</> })}
      fuss={(
        <>
          <KiKnopf art="primaer" deaktiviert={form.zustand !== "bereit"} onClick={() => { void save(); }}>{T.save}</KiKnopf>
          <KiKnopf deaktiviert={form.laeuft} onClick={() => { void leave(); }}>{T.cancel}</KiKnopf>
        </>
      )}>
      <Formular beschriftung="oben" {...form.formularProps}>
        <div className="sup__mask">
          {base !== null && (
            <p className="sup__muted">{T.currentTermLine(perCycleText(base.netAmount, base.cycle), formatDatum(base.validFrom))}</p>
          )}
          <TermFields draft={draft} onDraft={(patch) => setDraft((d) => ({ ...d, ...patch }))} rates={rateState} errors={errors}
            prefix="" withValidFrom />
          {current !== null && current.result !== null && (
            <p className="sup__preview" role="status">
              {current.result.firstDueDate === null ? T.effectiveNoCharge(formatDatum(current.result.validFrom))
                : T.effective(formatDatum(current.result.validFrom), formatDatum(current.result.firstDueDate))}
            </p>
          )}
        </div>
      </Formular>
    </Arbeitsdialog>
  );
}
